import type { Rng } from '../rng.js'
import { TEDDIES, TEDDY_KEYS, type TeddyKey } from '../teddies.js'
import type { SudokuShapeForLevel } from './sudoku-skills.js'
import {
  candidatesFor,
  EMPTY,
  shapeOf,
  valuesOf,
  solutionCount,
  type Grid,
  type SudokuSize,
} from './sudoku-solve.js'
import { solveByHand, type Technique } from './sudoku-techniques.js'

/**
 * Where a sudoku comes from.
 *
 * The rule this file exists to obey, in one line: **the generator never
 * decides that a puzzle is fair.** It takes a cell away and asks the solver how
 * many ways the grid can now be filled; unless the answer is exactly one, the
 * cell goes back.
 *
 * That is the same rule `riddle-puzzle.ts` is built on, and it is worth saying
 * why it is not merely tidy. A sudoku with two solutions is not a hard sudoku,
 * it is a broken one, and the child it breaks for is the one who filled it in
 * correctly and was told she had not. Nothing in a generator that marks its own
 * homework can catch that.
 *
 * The second rule is quieter and does more work: a puzzle is only kept if it
 * can be **finished by hand**, with the techniques its level allows. Brute
 * force will happily certify a unique puzzle whose only route through is
 * guessing and backing out again, which is a fair puzzle and a miserable one.
 */

/** How many teddies stand in for the numbers, at the sizes that use them. */
export const TEDDY_SIZES: readonly SudokuSize[] = [4, 6]

export interface SudokuPuzzle {
  readonly size: SudokuSize
  /** The grid as she is given it. `EMPTY` where she has to work. */
  readonly givens: Grid
  /** The truth. Never sent to a browser, and never used to mark a cell. */
  readonly solution: Grid
  /**
   * Which teddy each value is drawn as, value 1 first.
   *
   * Empty at 9×9, where a number is itself. The engine never reads this — it
   * is carried on the puzzle so that the seed decides it, and so that a round
   * replayed on the server deals the same faces it dealt in the browser.
   */
  readonly teddies: readonly TeddyKey[]
  /** The hardest technique needed to finish it, which is what it is really testing. */
  readonly hardest: Technique | null
}

/**
 * A full, valid grid.
 *
 * Backtracking with the candidates shuffled, so the seed decides the grid
 * rather than the search order always producing 1234 along the top row. It
 * cannot fail — an empty grid has a great many solutions and this walks
 * towards one of them — but it is written to report failure rather than to
 * assume, because "cannot fail" ages badly next to a changed box shape.
 */
export function fillGrid(size: SudokuSize, rng: Rng): number[] {
  const shape = shapeOf(size)
  const grid = new Array<number>(size * size).fill(EMPTY)

  const fill = (): boolean => {
    const cell = grid.indexOf(EMPTY)
    if (cell === -1) return true

    for (const value of rng.shuffle(candidatesFor(grid, shape, cell))) {
      grid[cell] = value
      if (fill()) return true
      grid[cell] = EMPTY
    }

    return false
  }

  if (!fill()) throw new Error(`Could not fill a ${size}×${size} grid`)

  return grid
}

/**
 * Takes cells away for as long as the puzzle stays fair and stays solvable.
 *
 * Every cell is tried, in an order the seed decides, and each is put straight
 * back if taking it away leaves either more than one solution or a grid that
 * cannot be finished by hand at this level. One pass rather than a search: a
 * second pass over the cells that were put back would find nothing, because
 * putting one back cannot make another removable.
 *
 * Symmetry is deliberately not attempted. A pretty pattern of holes is a
 * newspaper convention, and enforcing it means removing cells in pairs — which
 * refuses a hole for its partner's sake and makes the puzzle easier than the
 * level asked for, for a reason nobody playing it can see.
 */
export function digHoles(
  solution: Grid,
  shape: SudokuShapeForLevel,
  rng: Rng,
): { givens: number[]; hardest: Technique | null } {
  const geometry = shapeOf(shape.size)
  const givens = [...solution]
  let filled = givens.length
  let hardest: Technique | null = null

  for (const cell of rng.shuffle(givens.map((_, index) => index))) {
    if (filled <= shape.atLeast) break

    const was = givens[cell] as number
    givens[cell] = EMPTY

    /*
     * Fair first — and this check is not merely the cheaper filter it looks
     * like, so it must not be dropped as redundant.
     *
     * It is *nearly* redundant: `solveByHand` only ever makes forced
     * deductions, so anything it can finish has one answer, and the second
     * check below therefore implies this one. That is true exactly as long as
     * the three techniques are sound.
     *
     * Which is the point. Break one of them — the naked pair matching
     * overlapping candidates rather than identical ones is the classic way —
     * and the hand solver will happily "finish" a grid with two answers, and
     * every puzzle it certifies ships. Measured: with this line removed and
     * that one character changed, ambiguous puzzles appear at every size,
     * inside three hundred seeds.
     *
     * So this stays as the guarantee that does not depend on the clever code
     * being right. Cheap, and it is the one thing standing between a bug in a
     * technique and a child being told her correct grid is wrong.
     */
    if (solutionCount(givens, geometry, 2) !== 1) {
      givens[cell] = was
      continue
    }

    const byHand = solveByHand(givens, geometry, shape.allowed)
    if (!byHand.solved) {
      givens[cell] = was
      continue
    }

    filled -= 1
    hardest = byHand.hardest
  }

  return { givens, hardest }
}

/**
 * The teddies a board is drawn with.
 *
 * **No two share a species.** There are three rabbits, three squishmallows and
 * two turtles in the collection, and Stripey next to Bluey on a 60px cell is a
 * difference of colour rather than of shape — which would put a
 * visual-discrimination puzzle on top of a logic one and make the game harder
 * in a way that has nothing to do with sudoku.
 *
 * Sixteen species, so six is comfortable and the board is different every game.
 */
export function pickTeddies(size: SudokuSize, rng: Rng): TeddyKey[] {
  if (!TEDDY_SIZES.includes(size)) return []

  const chosen: TeddyKey[] = []
  const speciesTaken = new Set<string>()

  for (const key of rng.shuffle(TEDDY_KEYS)) {
    const species = TEDDIES[key].species
    if (speciesTaken.has(species)) continue

    speciesTaken.add(species)
    chosen.push(key)

    if (chosen.length === size) return chosen
  }

  throw new Error(`Only ${chosen.length} distinct species, needed ${size}`)
}

/**
 * A puzzle with exactly one answer, dug as deep as this level allows.
 *
 * There is no retry loop, and that is not an accident: every step here either
 * succeeds or leaves the grid as it was. The fill cannot fail, and the dig
 * cannot dig itself into an unfair puzzle, because it checks before keeping
 * each hole rather than after keeping several. So a seed is never a lottery
 * ticket that a sweep of three hundred has to be lucky to clear.
 */
export function buildSudoku(shape: SudokuShapeForLevel, rng: Rng): SudokuPuzzle {
  const solution = fillGrid(shape.size, rng)
  const { givens, hardest } = digHoles(solution, shape, rng)

  return {
    size: shape.size,
    givens,
    solution,
    teddies: pickTeddies(shape.size, rng),
    hardest,
  }
}

/** How many cells she has to fill in. The round's own measure of how far along she is. */
export function cellsToFill(puzzle: SudokuPuzzle): number {
  return puzzle.givens.filter((value) => value === EMPTY).length
}

export { valuesOf }
